import React, { useRef, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { updateModuleProgress } from '../../../SkillsApi/SkillsApi'
import './ModuleOne.css'

const MODULE_ID = 1
const SECTION = 'DroneSection'
// TODO: swap for the actual Drone Module 1 video URL
const VIDEO_URL = 'https://upskld-demo-videos.s3.ap-south-1.amazonaws.com/module_1_introduction_about_industry.mp4'

const ASSESSMENT_QUESTIONS = [
  {
    id: 1,
    question: 'What is a drone?',
    options: [
      { key: 'a', label: 'A helicopter with a pilot inside' },
      { key: 'b', label: 'An aircraft that flies without a pilot sitting inside it' },
      { key: 'c', label: 'A type of camera used only for weddings' },
      { key: 'd', label: 'A satellite that orbits the earth' },
    ],
    correctKey: 'b',
  },
  {
    id: 2,
    question: 'What are the two main types of drones, based on how they fly?',
    options: [
      { key: 'a', label: 'Rotary and Fixed-Wing' },
      { key: 'b', label: 'Manual and Digital' },
      { key: 'c', label: 'Indoor and Outdoor' },
      { key: 'd', label: 'Civilian and Military only' },
    ],
    correctKey: 'a',
  },
  {
    id: 3,
    question: 'Which of these is NOT one of the three broad categories of drone use?',
    options: [
      { key: 'a', label: 'Civilian' },
      { key: 'b', label: 'Defence' },
      { key: 'c', label: 'Humanitarian' },
      { key: 'd', label: 'Entertainment' },
    ],
    correctKey: 'd',
  },
  {
    id: 4,
    question: "What is the projected size of India's drone market by 2030?",
    options: [
      { key: 'a', label: '$61 Bn' },
      { key: 'b', label: '$1 Bn' },
      { key: 'c', label: '$3–5 Bn' },
      { key: 'd', label: '$41 Bn' },
    ],
    correctKey: 'c',
  },
]

const ModuleOne = () => {
  const navigate = useNavigate()
  const [answers, setAnswers] = useState({})
  const [watchPercent, setWatchPercent] = useState(0)
  const [videoEnded, setVideoEnded] = useState(false)
  const [finishing, setFinishing] = useState(false)

  const lastSentMilestoneRef = useRef(0)

  useEffect(() => {
    document.querySelector('#root')?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  // Same right-click / DevTools-shortcut deterrent as the other course modules.
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault()
      toast.warn('Right-click is disabled on this page.')
    }

    const handleKeyDown = (e) => {
      const isDevToolsShortcut =
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && ['I', 'J', 'C', 'i', 'j', 'c'].includes(e.key)) ||
        (e.ctrlKey && ['U', 'u'].includes(e.key))

      if (isDevToolsShortcut) {
        e.preventDefault()
        toast.warn('Inspect is disabled on this page.')
      }
    }

    document.addEventListener('contextmenu', handleContextMenu)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const sendMilestone = async (milestone) => {
    if (milestone <= lastSentMilestoneRef.current) return
    lastSentMilestoneRef.current = milestone
    await updateModuleProgress(SECTION, MODULE_ID, milestone)
  }

  // Milestones capped at 90 during playback — the final 100% is only sent
  // once the assessment is finished too (see handleFinish), so Module 2
  // only unlocks once BOTH the video and the quiz are done.
  const handleTimeUpdate = (e) => {
    const { currentTime, duration } = e.target
    if (!duration) return

    const percent = Math.min(100, Math.floor((currentTime / duration) * 100))
    setWatchPercent((prev) => (percent > prev ? percent : prev))

    const crossedMilestone = Math.min(90, Math.floor(percent / 10) * 10)
    if (crossedMilestone > lastSentMilestoneRef.current) {
      sendMilestone(crossedMilestone)
    }
  }

  const handleVideoEnded = () => {
    setWatchPercent(100)
    setVideoEnded(true)
    toast.info('Video watched! Now complete the assessment below.')
  }

  const handleAnswerSelect = (questionId, optionKey, correctKey) => {
    if (answers[questionId]) return

    setAnswers((prev) => ({ ...prev, [questionId]: optionKey }))

    if (optionKey === correctKey) {
      toast.success('Correct answer!')
    } else {
      toast.error('Wrong answer!')
    }
  }

  const allQuestionsAnswered = ASSESSMENT_QUESTIONS.every((q) => answers[q.id])
  const canFinish = videoEnded && allQuestionsAnswered

  const handleFinish = async () => {
    if (!canFinish || finishing) return
    setFinishing(true)
    await sendMilestone(100)
    toast.success('Module 1 complete — Module 2 unlocked!')
    navigate('/drone_course')
  }

  return (
    <div className="module_one_pg_wrapper">
      <ToastContainer position="top-right" autoClose={2000} />

      <div className="module_one_pg_content">
        <h2 className="module_one_pg_title">Module 1: Getting Started</h2>
        <p className="module_one_pg_subtitle">
          Watch the full video, then complete the short assessment to unlock Module 2.
        </p>

        <div className="module_one_pg_video_wrap">
          <video
            className="module_one_pg_video"
            src={VIDEO_URL}
            controls
            controlsList="nodownload"
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleVideoEnded}
          />
        </div>

        <div className="module_one_pg_progress_row">
          <div className="module_one_pg_progress_track">
            <div
              className="module_one_pg_progress_fill"
              style={{ width: `${watchPercent}%` }}
            />
          </div>
          <span className="module_one_pg_progress_label">{watchPercent}% watched</span>
        </div>

        <div className="module_one_pg_assessment">
          <h3 className="module_one_pg_assessment_title">Module Assessment</h3>
          <p className="module_one_pg_assessment_sub">
            {videoEnded
              ? `Answer all ${ASSESSMENT_QUESTIONS.length} questions to complete this module.`
              : 'Finish watching the video above to unlock the assessment.'}
          </p>

          {ASSESSMENT_QUESTIONS.map((q, qIndex) => {
            const selected = answers[q.id]

            return (
              <div className="module_one_pg_question_card" key={q.id}>
                <p className="module_one_pg_question_text">
                  <span className="module_one_pg_question_num">Q{qIndex + 1}.</span> {q.question}
                </p>

                <div className="module_one_pg_options_grid">
                  {q.options.map((opt) => {
                    const isSelected = selected === opt.key
                    const isCorrectOpt = opt.key === q.correctKey
                    let stateClass = ''

                    if (selected) {
                      if (isSelected && isCorrectOpt) stateClass = 'module_one_pg_option_correct'
                      else if (isSelected && !isCorrectOpt) stateClass = 'module_one_pg_option_wrong'
                      else if (!isSelected && isCorrectOpt) stateClass = 'module_one_pg_option_reveal'
                    }

                    return (
                      <button
                        type="button"
                        key={opt.key}
                        className={`module_one_pg_option_btn ${stateClass}`}
                        onClick={() => handleAnswerSelect(q.id, opt.key, q.correctKey)}
                        disabled={!videoEnded || !!selected}
                      >
                        <span className="module_one_pg_option_key">{opt.key.toUpperCase()}</span>
                        <span className="module_one_pg_option_label">{opt.label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}

          <button
            type="button"
            className="module_one_pg_complete_btn"
            onClick={handleFinish}
            disabled={!canFinish || finishing}
          >
            {finishing ? 'Saving...' : (
              <>
                Complete Module <span className="module_one_pg_complete_check">&#10003;</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ModuleOne